import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Compass, Keyboard, LifeBuoy, Search, ShieldCheck, Lightbulb } from 'lucide-react';
import { PageHeader } from '@/components/wingo/ui/page-header';
import { Input } from '@/components/wingo/ui/input';
import { Card } from '@/components/wingo/ui/card';
import { Accordion, type AccordionItemData } from '@/components/wingo/ui/accordion';
import { Badge } from '@/components/wingo/ui/badge';
import { Button } from '@/components/wingo/ui/button';
import { Kbd } from '@/components/wingo/ui/kbd';
import { EmptyState } from '@/components/wingo/ui/empty-state';
import { DescriptionList, type DescriptionListItem } from '@/components/wingo/ui/description-list';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDirection } from '@/contexts/DirectionContext';
import { useSearchPalette } from '@/features/search/hooks/useSearchPalette';
import { useOnboardingTour } from '../tour/OnboardingTourProvider';
import {
  HELP_FAQ,
  HELP_GLOSSARY,
  HELP_GUIDES,
  HELP_SHORTCUTS,
  HELP_STATUSES,
  matchesDirection,
  normalizeForSearch,
  pick,
  type HelpGuide,
  type HelpLang,
} from '../content/helpContent';

/**
 * Centrul de ajutor al portalului (/app/help): turul ghidat, ghidul fiecărei pagini, statusuri,
 * glosar, scurtături și întrebări frecvente. Compus din componente Wingo UI (components/wingo).
 * Conținutul stă în content/helpContent.ts; aici e doar prezentarea și căutarea.
 */

const COPY = {
  title: { ro: 'Ajutor și ghid de utilizare', en: 'Help and user guide' },
  description: {
    ro: 'Tot ce trebuie să știi ca să folosești portalul TLS: turul ghidat, ghidul fiecărei pagini și răspunsuri la întrebările frecvente.',
    en: 'Everything you need to use the TLS portal: the guided tour, a guide for every page and answers to common questions.',
  },
  startTour: { ro: 'Pornește turul ghidat', en: 'Start the guided tour' },
  searchPlaceholder: { ro: 'Caută în ghid: export, avizare, 2FA, status…', en: 'Search the guide: export, arrival notice, 2FA, status…' },
  searchLabel: { ro: 'Caută în ghid', en: 'Search the guide' },
  clear: { ro: 'Șterge căutarea', en: 'Clear search' },
  quickTour: { ro: 'Tur ghidat', en: 'Guided tour' },
  quickTourSub: { ro: 'Un minut, pe ecranul tău, pas cu pas.', en: 'One minute, on your screen, step by step.' },
  quickSearch: { ro: 'Căutare rapidă', en: 'Quick search' },
  quickSearchSub: { ro: 'Containere, B/L-uri, oferte, nave, firme.', en: 'Containers, bills of lading, offers, vessels, companies.' },
  quickSecurity: { ro: 'Securizează contul', en: 'Secure your account' },
  quickSecuritySub: { ro: 'Activează autentificarea în doi pași.', en: 'Turn on two-step verification.' },
  guidesTitle: { ro: 'Ghid pe pagini', en: 'Guide by page' },
  guidesSub: { ro: 'Deschide o secțiune ca să vezi ce poți face acolo.', en: 'Open a section to see what you can do there.' },
  openPage: { ro: 'Deschide pagina', en: 'Open the page' },
  tip: { ro: 'Bine de știut', en: 'Good to know' },
  statusesTitle: { ro: 'Ce înseamnă statusurile', en: 'What the statuses mean' },
  statusesSub: { ro: 'Pentru direcția selectată acum.', en: 'For the direction selected now.' },
  glossaryTitle: { ro: 'Glosar', en: 'Glossary' },
  glossarySub: { ro: 'Termenii care apar în portal.', en: 'The terms used in the portal.' },
  shortcutsTitle: { ro: 'Scurtături de tastatură', en: 'Keyboard shortcuts' },
  shortcutsSub: { ro: 'Pe calculator, din orice pagină.', en: 'On a computer, from any page.' },
  faqTitle: { ro: 'Întrebări frecvente', en: 'Frequently asked questions' },
  contactTitle: { ro: 'Nu ai găsit răspunsul?', en: 'Didn’t find the answer?' },
  contactSub: {
    ro: 'Scrie-i agentului TLS cu care lucrezi: îți răspunde despre transporturi, documente, oferte și accesul la cont.',
    en: 'Write to the TLS agent you work with: they can help with shipments, documents, offers and account access.',
  },
  noResults: { ro: 'Nimic găsit în ghid', en: 'Nothing found in the guide' },
  noResultsSub: { ro: 'Încearcă alt cuvânt sau pornește turul ghidat.', en: 'Try another word or start the guided tour.' },
  results: { ro: 'rezultate', en: 'results' },
} as const;

function guideText(guide: HelpGuide, lang: HelpLang): string {
  return [guide.title, guide.summary, ...guide.steps, ...(guide.tips ?? [])].map((text) => pick(text, lang)).join(' ');
}

/** Card de acces rapid: iconiță în tile colorat + titlu și descriere (Card Wingo, conținut compus). */
function QuickCard({
  icon,
  tileClassName,
  title,
  description,
  aside,
}: {
  icon: React.ReactNode;
  tileClassName: string;
  title: string;
  description: string;
  aside?: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className={`grid size-10 shrink-0 place-items-center rounded-xl [&_svg]:size-5 ${tileClassName}`}>{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold text-foreground">{title}</span>
          {aside}
        </div>
        <p className="mt-0.5 text-[13px] leading-snug text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

function SectionTitle({ icon, title, sub }: { icon: React.ReactNode; title: string; sub?: string }) {
  return (
    <div className="mb-3 flex items-start gap-3">
      <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-brand-soft text-brand-soft-foreground [&_svg]:size-[18px]">
        {icon}
      </span>
      <div className="min-w-0">
        <h2 className="text-base font-semibold tracking-tight text-foreground">{title}</h2>
        {sub ? <p className="mt-0.5 text-[13px] text-muted-foreground">{sub}</p> : null}
      </div>
    </div>
  );
}

const HelpCenter: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const lang: HelpLang = currentLanguage === 'en' ? 'en' : 'ro';
  const c = (key: keyof typeof COPY) => COPY[key][lang];
  const { direction } = useDirection();
  const { startTour } = useOnboardingTour();
  const { open: openSearch } = useSearchPalette();
  const [query, setQuery] = useState('');

  // Fundalul paginilor portalului (var(--bg) din claude-design), ca la dashboard / oferte.
  useEffect(() => {
    document.body.classList.add('dashboard-claude-bg');
    return () => {
      document.body.classList.remove('dashboard-claude-bg');
    };
  }, []);

  const needle = normalizeForSearch(query.trim());
  const matches = (text: string) => !needle || normalizeForSearch(text).includes(needle);

  const guides = useMemo(
    () => HELP_GUIDES.filter((guide) => matchesDirection(guide.directions, direction)).filter((guide) => matches(guideText(guide, lang))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [direction, lang, needle],
  );
  const faq = useMemo(
    () =>
      HELP_FAQ.filter((item) => matchesDirection(item.directions, direction)).filter((item) =>
        matches(`${pick(item.question, lang)} ${pick(item.answer, lang)}`),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [direction, lang, needle],
  );
  const statuses = useMemo(
    () =>
      HELP_STATUSES.filter((status) => status.directions.includes(direction)).filter((status) =>
        matches(`${pick(status.label, lang)} ${pick(status.definition, lang)}`),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [direction, lang, needle],
  );
  const glossary = useMemo(
    () => HELP_GLOSSARY.filter((term) => matches(`${pick(term.term, lang)} ${pick(term.definition, lang)}`)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, needle],
  );
  const shortcuts = useMemo(
    () => HELP_SHORTCUTS.filter((shortcut) => matches(`${pick(shortcut.label, lang)} ${shortcut.keys.join(' ')}`)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, needle],
  );

  const resultCount = guides.length + faq.length + statuses.length + glossary.length + shortcuts.length;
  const searching = needle.length > 0;

  const guideItems: AccordionItemData[] = guides.map((guide) => {
    const Icon = guide.icon;
    return {
      value: guide.id,
      icon: <Icon />,
      title: pick(guide.title, lang),
      description: pick(guide.summary, lang),
      content: (
        <div className="space-y-4">
          <ol className="space-y-2.5">
            {guide.steps.map((step, index) => (
              <li key={index} className="flex gap-3">
                <span className="grid size-6 shrink-0 place-items-center rounded-full bg-foreground/[0.06] text-xs font-semibold tabular-nums text-foreground">
                  {index + 1}
                </span>
                <span className="pt-0.5 leading-relaxed">{pick(step, lang)}</span>
              </li>
            ))}
          </ol>
          {guide.tips?.length ? (
            <div className="flex gap-2.5 rounded-xl bg-brand-soft px-3.5 py-3 text-brand-soft-foreground">
              <Lightbulb className="mt-0.5 size-4 shrink-0" />
              <div className="space-y-1">
                <div className="text-xs font-semibold uppercase tracking-wide">{c('tip')}</div>
                {guide.tips.map((tip, index) => (
                  <p key={index} className="leading-relaxed">
                    {pick(tip, lang)}
                  </p>
                ))}
              </div>
            </div>
          ) : null}
          {guide.to ? (
            <Button asChild variant="soft" size="sm" rightIcon={<ArrowRight />}>
              <Link to={guide.to}>{c('openPage')}</Link>
            </Button>
          ) : null}
        </div>
      ),
    };
  });

  const faqItems: AccordionItemData[] = faq.map((item) => ({
    value: item.id,
    title: pick(item.question, lang),
    content: <p className="leading-relaxed">{pick(item.answer, lang)}</p>,
  }));

  const statusItems: DescriptionListItem[] = statuses.map((status) => ({
    id: status.id,
    label: (
      <Badge tone={status.tone} color={status.color} variant="soft" dot>
        {pick(status.label, lang)}
      </Badge>
    ),
    value: pick(status.definition, lang),
  }));

  const glossaryItems: DescriptionListItem[] = glossary.map((term) => ({
    id: term.id,
    label: pick(term.term, lang),
    value: pick(term.definition, lang),
  }));

  return (
    <div className="mx-auto w-full max-w-5xl space-y-8 pb-10">
      <PageHeader
        icon={<LifeBuoy />}
        tone="brand"
        title={c('title')}
        description={c('description')}
        actions={[{ id: 'tour', label: c('startTour'), icon: <Compass />, primary: true, tone: 'brand', onSelect: startTour }]}
      />

      <Input
        type="search"
        aria-label={c('searchLabel')}
        placeholder={c('searchPlaceholder')}
        value={query}
        onValueChange={setQuery}
        leftIcon={<Search />}
        clearable
        clearLabel={c('clear')}
        suffix={searching ? `${resultCount} ${c('results')}` : undefined}
        size="lg"
      />

      {!searching && (
        <div className="grid gap-3 sm:grid-cols-3">
          <Card variant="outline" interactive onClick={startTour}>
            <QuickCard
              icon={<Compass />}
              tileClassName="bg-brand-soft text-brand-soft-foreground"
              title={c('quickTour')}
              description={c('quickTourSub')}
            />
          </Card>
          <Card variant="outline" interactive onClick={openSearch}>
            <QuickCard
              icon={<Search />}
              tileClassName="bg-info-soft text-info-soft-foreground"
              title={c('quickSearch')}
              description={c('quickSearchSub')}
              aside={<Kbd shortcut="mod+k" size="sm" hideOnTouch />}
            />
          </Card>
          <Card variant="outline" asChild>
            <Link to="/app/two-factor">
              <QuickCard
                icon={<ShieldCheck />}
                tileClassName="bg-success-soft text-success-soft-foreground"
                title={c('quickSecurity')}
                description={c('quickSecuritySub')}
              />
            </Link>
          </Card>
        </div>
      )}

      {searching && resultCount === 0 ? (
        <EmptyState
          preset="no-results"
          variant="dashed"
          title={c('noResults')}
          description={c('noResultsSub')}
          actions={
            <Button tone="brand" leftIcon={<Compass />} onClick={startTour}>
              {c('startTour')}
            </Button>
          }
        />
      ) : null}

      {guideItems.length > 0 && (
        <section>
          <SectionTitle icon={<BookOpen />} title={c('guidesTitle')} sub={c('guidesSub')} />
          <Accordion
            items={guideItems}
            type="multiple"
            variant="separated"
            tone="brand"
            keepMounted
            headingLevel={3}
            defaultValue={searching ? guideItems.map((item) => item.value) : []}
            key={searching ? `search-${needle}` : 'browse'}
          />
        </section>
      )}

      {(statusItems.length > 0 || glossaryItems.length > 0) && (
        <div className="grid gap-6 lg:grid-cols-2">
          {statusItems.length > 0 && (
            <Card variant="outline" title={c('statusesTitle')} description={c('statusesSub')}>
              <DescriptionList items={statusItems} layout="stacked" variant="divided" size="sm" />
            </Card>
          )}
          {glossaryItems.length > 0 && (
            <Card variant="outline" title={c('glossaryTitle')} description={c('glossarySub')}>
              <DescriptionList items={glossaryItems} layout="stacked" variant="divided" size="sm" />
            </Card>
          )}
        </div>
      )}

      {shortcuts.length > 0 && (
        <Card
          variant="outline"
          className="hidden md:flex"
          title={
            <span className="flex items-center gap-2">
              <Keyboard className="size-4 text-muted-foreground" />
              {c('shortcutsTitle')}
            </span>
          }
          description={c('shortcutsSub')}
        >
          <DescriptionList
            columns={2}
            variant="divided"
            size="sm"
            items={shortcuts.map((shortcut) => ({
              id: shortcut.id,
              label: pick(shortcut.label, lang),
              // combinațiile cu „mod” trec prin `shortcut` (⌘ pe Mac, Ctrl în rest); restul sunt taste simple
              value: shortcut.keys.includes('mod') ? (
                <Kbd shortcut={shortcut.keys.join('+').toLowerCase()} size="sm" />
              ) : (
                <Kbd keys={shortcut.keys} size="sm" />
              ),
            }))}
          />
        </Card>
      )}

      {faqItems.length > 0 && (
        <section>
          <SectionTitle icon={<LifeBuoy />} title={c('faqTitle')} />
          <Accordion
            items={faqItems}
            variant="outline"
            keepMounted
            headingLevel={3}
            type={searching ? 'multiple' : 'single'}
            defaultValue={searching ? faqItems.map((item) => item.value) : undefined}
            key={searching ? `faq-${needle}` : 'faq'}
          />
        </section>
      )}

      {!searching && (
        <EmptyState
          variant="surface"
          align="start"
          size="sm"
          icon={<LifeBuoy />}
          tone="brand"
          title={c('contactTitle')}
          description={c('contactSub')}
        />
      )}
    </div>
  );
};

export default HelpCenter;
